<script setup>
import { countries } from '~/utils'

const { $pb } = useNuxtApp()

definePageMeta({
  layout: 'public'
})

const { bg } = useBackground()
bg.value = 'dimmed'

const limit = ref(12)
const loading = ref(false)
const loadingMore = ref(false)
const search = ref('')
const startupsList = ref([])
const totalStartups = ref(0)
const currentPage = ref(1)

async function loadStartups(page = 1, append = false) {
  if (append) {
    loadingMore.value = true
  } else {
    loading.value = true
  }

  try {
    const filter = search.value
      ? `(name ~ "${search.value}" || location.city ~ "${search.value}") && verified = true`
      : 'verified = true'

    const result = await $pb.collection('startups').getList(page, limit.value, {
      filter,
      expand: 'location',
      sort: '-created'
    })

    if (append) {
      startupsList.value.push(...result.items)
    } else {
      startupsList.value = result.items
    }

    totalStartups.value = result.totalItems
    currentPage.value = page
  } catch (err) {
    console.error('Error loading startups:', err)
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

// Initial load
await loadStartups()

// Watch for search changes and reload
watch(search, () => {
  currentPage.value = 1
  loadStartups(1, false)
})

const head = {
  title: `Startups | BuiltByFew`,
  description: `Browse our ${totalStartups.value} amazing startups`
}

defineOgImageComponent('OgImage', {
  title: head.title,
  description: head.description,
})

async function loadMoreStartups() {
  await loadStartups(currentPage.value + 1, true)
}

// Subscribe to realtime updates
onMounted(() => {
  $pb.collection('startups').subscribe('*', () => {
    loadStartups(currentPage.value, false)
  })

  if (import.meta.client) {
    nextTick(() => {
      if (bg.value !== 'dimmed') {
        bg.value = 'dimmed'
      }
    })
  }
})

// Cleanup subscription on component unmount
onUnmounted(() => {
  $pb.collection('startups').unsubscribe()
})

function fileUrl(record, fileField, size = '80x80') {
  if (!record || !fileField) {
    return null
  }
  return $pb.files.getURL(record, record[fileField], {
    thumb: size
  })
}

function logoPreview(startup) {
  if (startup?.logo) {
    return $pb.files.getURL(startup, startup.logo, {
      'thumb': '200x200'
    })
  }
  return null
}

function heroPreview(startup) {
  if (!startup?.hero) {
    return null
  }
  return $pb.files.getURL(startup, startup.hero)
}

function getCountryName(countryCode) {
  return countries.find(country => country.value === countryCode).name
}

const searchInput = useTemplateRef('searchInput')

defineShortcuts({
  '/': () => {
    searchInput.value.inputRef.focus()
  }
})

</script>

<template>
  <!-- Hero Section -->
  <div class="relative overflow-hidden">
    <UContainer class="py-16 md:py-24">
      <div class="max-w-4xl mx-auto text-center">
        <HeroText
          title="Connect"
          :subtitle="['with innovative', 'startups ready to hire.']"
          subtitle-width="w-96"
        />
        <div class="flex flex-col items-center">
          <div class="brunson text-2xl text-white mb-8 max-w-xl mx-auto tracking-wider bg-(--ui-bg-inverted) dark:bg-(--ui-bg) [box-decoration:clone] p-4">
            Search for startups by name, location, or industry.
            <div class="mt-4 w-full mx-auto relative">
              <UInput
                ref="searchInput"
                v-model="search"
                placeholder="Search for a startup"
                class="w-full"
                size="xl"
              />
              <UButton
                v-if="search"
                icon="i-heroicons-x-mark"
                color="neutral"
                variant="ghost"
                size="sm"
                class="absolute right-2 top-1/2 -translate-y-1/2"
                @click="search = ''"
              />
            </div>
          </div>
        </div>
      </div>
    </UContainer>
  </div>

  <div v-if="loading" class="w-full">
    <UContainer>
      <div class="flex justify-center items-center pb-4 text-(--ui-text-toned) text-sm">
        Searching for startups...
      </div>
      <UProgress indeterminate />
    </UContainer>
  </div>

  <div v-else class="relative w-full mb-10 px-4 md:px-0">
    <UContainer v-if="startupsList.length > 0" class="py-8 bg-(--ui-bg)">
      <div class="grid grid-cols-12 gap-4">
        <div v-for="startup in startupsList" :key="startup.id" class="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-4">
          <NuxtLink :to="`/startups/${startup.slug}`">
            <div class="flex items-center gap-4 min-h-32 border border-(--ui-border) hover:border-(--ui-border-hover) transition-colors">
              <div class="relative w-12 h-12 overflow-hidden bg-neutral border border-(--ui-border-muted) ml-4">
                <div class="absolute inset-0 flex justify-center items-center">
                  <img :src="logoPreview(startup)" :alt="startup.name" class="w-full h-full object-contain" />
                </div>
              </div>
              <div class="flex flex-col items-start max-w-64">
                <div class="text-(--ui-text) text-2xl font-bold max-w-64 truncate">
                  {{ startup.name }}
                </div>
                <div class="text-(--ui-text-muted) text-sm truncate whitespace-nowrap max-w-full">
                  {{ startup.expand.location.city }}, {{ getCountryName(startup.expand.location.country) }}
                </div>
              </div>
            </div>
          </NuxtLink>
        </div>
      </div>

      <div v-if="totalStartups > startupsList.length" class="flex justify-center items-center mt-8">
        <UButton
          :label="loadingMore ? 'Loading...' : 'Load More Startups'"
          :loading="loadingMore"
          color="neutral"
          variant="solid"
          @click="loadMoreStartups"
        />
      </div>
    </UContainer>

    <UContainer v-else-if="!loading" class="py-8">
      <div class="text-center text-(--ui-text-muted)">
        <p class="text-lg">No startups found matching your search.</p>
      </div>
    </UContainer>
  </div>
</template>
